import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined'
import { Box, IconButton, Typography } from '@mui/material'
import { useState } from 'react'
import { useTranslate } from 'react-admin'

import { useLocalizedText } from '../../domain/announcements'

const ARROW_SX = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  bgcolor: 'background.paper',
  boxShadow: 2,
  '&:hover': { bgcolor: 'background.paper' },
}

const StepDots = ({ count, index, onSelect }) => {
  const translate = useTranslate()

  return (
    <Box sx={{ display: 'flex', gap: 0.75 }}>
      {Array.from({ length: count }, (_, dotIndex) => (
        <Box
          key={dotIndex}
          component="button"
          type="button"
          onClick={() => onSelect(dotIndex)}
          aria-label={translate('mymove.whatsNew.picture', { number: dotIndex + 1 })}
          aria-current={dotIndex === index ? 'step' : undefined}
          sx={{
            width: dotIndex === index ? 20 : 8,
            height: 8,
            p: 0,
            border: 0,
            borderRadius: 4,
            cursor: 'pointer',
            bgcolor: dotIndex === index ? 'primary.main' : 'action.disabled',
            transition: 'width .2s',
          }}
        />
      ))}
    </Box>
  )
}

const PictureSteps = ({ pictures }) => {
  const translate = useTranslate()
  const localize = useLocalizedText()
  const [index, setIndex] = useState(0)
  const current = pictures[index]
  const hasSeveral = pictures.length > 1

  if (!current) {
    return <NewReleasesOutlinedIcon sx={{ fontSize: 72, color: 'text.disabled' }} />
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ position: 'relative' }}>
        <Box
          component="img"
          src={current.url}
          alt=""
          sx={{ display: 'block', width: '100%', borderRadius: 2, boxShadow: 3, objectFit: 'contain' }}
        />
        {hasSeveral && (
          <>
            <IconButton
              size="small"
              disabled={index === 0}
              onClick={() => setIndex(index - 1)}
              aria-label={translate('mymove.whatsNew.previousPicture')}
              sx={{ ...ARROW_SX, left: -16 }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              size="small"
              disabled={index === pictures.length - 1}
              onClick={() => setIndex(index + 1)}
              aria-label={translate('mymove.whatsNew.nextPicture')}
              sx={{ ...ARROW_SX, right: -16 }}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        )}
      </Box>
      {(hasSeveral || current.caption_i18n) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
          {hasSeveral && <StepDots count={pictures.length} index={index} onSelect={setIndex} />}
          {current.caption_i18n && (
            <Typography variant="body2" color="text.secondary" data-testid="picture-caption">
              {hasSeveral && `${index + 1}. `}
              {localize(current.caption_i18n)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

export default PictureSteps
